import type { UserRole } from '../types/config';

export function normalizeUiRole(value: string | null | undefined): UserRole | null {
  const role = (value ?? '').trim().toLowerCase().replace(/[\s-]+/g, '_');
  if (!role) return null;
  if (role === 'super_admin' || role === 'superadmin') return 'super_admin';
  if (role === 'admin' || role === 'administrator') return 'admin';
  if (role === 'teacher' || role === 'staff') return 'teacher';
  if (
    role === 'student' ||
    role === 'parent' ||
    role === 'guardian' ||
    role === 'student_portal' ||
    role === 'parent_portal' ||
    role === 'student_portal_user'
  ) {
    return 'student';
  }
  return null;
}

const SUPER_ADMIN_EMAIL_HINT =
  /(^|[._-])super[._-]?admin(@|[._-])|superadmin@|@superadmin\./i;

export function inferUiRoleFromEmail(email: string): UserRole {
  const e = email.trim().toLowerCase();
  if (SUPER_ADMIN_EMAIL_HINT.test(e)) return 'super_admin';
  if (e.includes('student') || e.includes('parent') || e.includes('guardian')) return 'student';
  if (e.includes('teacher')) return 'teacher';
  return 'admin';
}

/** Map backend permission codes to the UI role used for dashboards and legacy fallbacks. */
export function resolveUiRoleFromPermissions(permissions: string[]): UserRole {
  if (
    permissions.includes('role:permission:assign') ||
    permissions.includes('subscription:manage') ||
    permissions.includes('fees:manage') ||
    permissions.includes('staff:manage')
  ) {
    return 'admin';
  }
  if (
    permissions.some(
      (permission) =>
        permission.startsWith('teacher_portal:') ||
        permission === 'attendance:manage' ||
        permission === 'assignment:grade' ||
        permission === 'assignment:create'
    )
  ) {
    return 'teacher';
  }
  if (
    permissions.includes('portal:parent') ||
    permissions.includes('portal:student') ||
    permissions.includes('assignment:submit')
  ) {
    return 'student';
  }
  if (
    permissions.includes('fees:read') &&
    !permissions.includes('fees:manage') &&
    !permissions.includes('student:read') &&
    !permissions.includes('staff:manage')
  ) {
    return 'student';
  }
  return 'admin';
}

/** Resolve UI role for login/session (email pattern wins for portal users). */
export function resolveUserRole(
  input: { email?: string; role?: string; permissions?: string[] },
  emailHint?: string
): UserRole {
  const email = (input.email ?? emailHint ?? '').trim().toLowerCase();
  if (inferUiRoleFromEmail(email) === 'student') return 'student';

  const explicit =
    normalizeUiRole(input.role) ??
    normalizeUiRole(input.permissions?.find((p) => p.startsWith('role:')));

  const permissions = input.permissions ?? [];
  if (permissions.length > 0) {
    const fromPermissions = resolveUiRoleFromPermissions(permissions);
    if (fromPermissions === 'student') return 'student';
    if (explicit) return explicit;
    return fromPermissions;
  }

  return explicit ?? inferUiRoleFromEmail(email);
}

export function roleLabel(role: string): string {
  return role.replace(/_/g, ' ');
}

/**
 * Permissions that identify a true tenant admin / owner.
 * Login often omits role_name, and `resolveUiRoleFromPermissions` collapses
 * office_staff / receptionist / accountant to UI role `admin` — so role alone
 * must not unlock CSV / tabular data export.
 */
const TENANT_ADMIN_EXPORT_PERMISSIONS = [
  'role:permission:assign',
  'role:assign',
  'role:create',
  'subscription:manage',
  'user:create',
] as const;

/** Role names that may never export tabular data (CSV/Excel/data packs). PDF + due slips stay allowed. */
const DATA_EXPORT_DENIED_ROLE_NAMES = new Set([
  'transport_manager',
  'librarian',
  'office_staff',
  'receptionist',
  'accountant',
  'hostel_warden',
  'teacher',
  'staff',
  'student',
  'student_portal_user',
  'parent',
  'guardian',
]);

const DATA_EXPORT_ALLOWED_ROLE_NAMES = new Set([
  'owner',
  'admin',
  'administrator',
  'super_admin',
  'superadmin',
]);

function normalizeRoleName(value: string | null | undefined): string {
  return (value ?? '').trim().toLowerCase().replace(/[\s-]+/g, '_');
}

function hasTenantAdminExportPermission(permissions: string[] | null | undefined): boolean {
  if (!permissions?.length) return false;
  return TENANT_ADMIN_EXPORT_PERMISSIONS.some((code) => permissions.includes(code));
}

function collectRoleNames(user: {
  role?: string | null;
  roleNames?: string[] | null;
}): string[] {
  const names = (user.roleNames ?? []).map(normalizeRoleName).filter(Boolean);
  const raw = normalizeRoleName(user.role);
  // Include non-collapsed raw role strings (e.g. transport_manager) when present.
  if (raw && !['admin', 'teacher', 'student', 'super_admin'].includes(raw)) {
    names.push(raw);
  }
  return names;
}

/**
 * Tabular / pack data export (CSV, Excel, data-exit) — admin/owner/super_admin only.
 * Transport managers, librarians, and other staff roles are denied.
 * PDF documents and fee due slips are not gated by this helper.
 */
export function canExportPeopleDirectoryCsv(
  user?: {
    role?: string | null;
    roleNames?: string[] | null;
    permissions?: string[] | null;
  } | null
): boolean {
  if (!user) return false;

  const uiRole = normalizeUiRole(user.role);
  if (uiRole === 'super_admin') return true;

  const names = collectRoleNames(user);
  if (names.some((n) => DATA_EXPORT_ALLOWED_ROLE_NAMES.has(n))) return true;
  if (names.some((n) => DATA_EXPORT_DENIED_ROLE_NAMES.has(n))) return false;

  // No usable role names (older sessions): require tenant-admin permissions.
  return hasTenantAdminExportPermission(user.permissions);
}

/** Alias — same rule for any non-PDF / non-due-slip data export. */
export const canExportTabularData = canExportPeopleDirectoryCsv;
